import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { contractApi } from '../../../services/contractApi';
import { personApi } from '../../../services/personApi';
import { classifyTaxDocument, formatCpfCnpj, normalizeSignerRole } from './ContractSignPage.helpers';

interface SignerValidation {
    name: string; govTaxId: string; role: string; valid: boolean; email?: string; phone?: string; reason?: string;
}

interface ConfirmStepProps {
    contractId: string;
    extractedData: Record<string, unknown> | null;
    onComplete: () => void;
    onBack: () => void;
    setLoading: (v: boolean) => void;
}

const ConfirmStep = ({ contractId, extractedData, onComplete, onBack, setLoading }: ConfirmStepProps) => {
    const [signerValidations, setSignerValidations] = useState<SignerValidation[]>([]);
    const [validatingSigners, setValidatingSigners] = useState(false);
    const [contractValidation, setContractValidation] = useState<{ valid: boolean; canSendToSignature: boolean; issues: any[] } | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        loadContractValidation();
        validateSigners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractId]);

    const loadContractValidation = async () => {
        try {
            const result = await contractApi.getContractPageForm(contractId);
            setContractValidation(result.validation);
        } catch { /* skip */ }
    };

    const validateSigners = async () => {
        if (!extractedData?.signers || !Array.isArray(extractedData.signers)) return;
        setValidatingSigners(true);
        const signers = extractedData.signers as { name: string; gov_tax_id?: string; role: string }[];
        const results: SignerValidation[] = [];

        for (const signer of signers) {
            const cpfCnpj = (signer.gov_tax_id || '').replace(/\D/g, '');
            if (!cpfCnpj) { results.push({ name: signer.name, govTaxId: '', role: signer.role, valid: false, reason: 'CPF/CNPJ não informado' }); continue; }
            try {
                const docType = classifyTaxDocument(cpfCnpj);
                if (docType === 'INVALID') {
                    results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: false, reason: 'CPF/CNPJ inválido' });
                    continue;
                }
                if (docType === 'CPF') {
                    const person = await personApi.searchIndividualByCpf(cpfCnpj);
                    if (!person) { results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: false, reason: 'Pessoa não encontrada' }); continue; }
                    if (!person.email) { results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: false, reason: 'Email não cadastrado' }); continue; }
                    results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: true, email: person.email, phone: person.mobile });
                } else {
                    const legalPerson = await personApi.searchLegalPersonByCnpj(cpfCnpj);
                    if (!legalPerson) { results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: false, reason: 'Empresa não encontrada' }); continue; }
                    const signatories = await personApi.getSignatories(legalPerson.personId);
                    const validSig = signatories.find((s: any) => s.isActive && s.email);
                    if (!validSig) { results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: false, reason: 'Nenhum signatário ativo com email' }); continue; }
                    results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: true, email: validSig.email, phone: validSig.mobile });
                }
            } catch { results.push({ name: signer.name, govTaxId: cpfCnpj, role: signer.role, valid: false, reason: 'Erro ao consultar Person' }); }
        }
        setSignerValidations(results);
        setValidatingSigners(false);
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        setLoading(true);
        try {
            const signers = signerValidations
                .filter(s => s.valid)
                .map(s => ({ name: s.name, email: s.email, role: normalizeSignerRole(s.role), govTaxId: s.govTaxId, phone: s.phone }));

            await contractApi.updateContract(contractId, { signers: signers as Record<string, unknown>[] });
            toast.success('Dados confirmados');
            onComplete();
        } catch (e: any) {
            toast.error(e.message || 'Erro ao confirmar');
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    const allSignersValid = signerValidations.length > 0 && signerValidations.every(s => s.valid);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Validação e Confirmação</h2>

            {/* Contract structure validation */}
            {contractValidation && !contractValidation.valid && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-300">⚠️ Estrutura incompleta:</p>
                    {contractValidation.issues.map((issue: any, i: number) => (
                        <p key={i} className="text-xs text-red-600 dark:text-red-400 ml-4">{issue.message}</p>
                    ))}
                </div>
            )}

            {/* Signers validation */}
            <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Signatários</h3>
                {validatingSigners && <p className="text-sm text-gray-500 mb-2">Validando signatários...</p>}
                <div className="space-y-2">
                    {signerValidations.map((s, i) => (
                        <div key={i} className={`flex flex-col gap-1 p-3 rounded-lg text-sm border ${s.valid ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'}`}>
                            <div className="flex items-center gap-3">
                                <span>{s.valid ? '✅' : '❌'}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{s.name}</span>
                                <span className="text-gray-500 dark:text-gray-400">{formatCpfCnpj(s.govTaxId)}</span>
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs">{s.role}</span>
                            </div>
                            {s.valid && s.email && <span className="ml-8 text-xs text-gray-600 dark:text-gray-400">📧 {s.email}</span>}
                            {!s.valid && <span className="ml-8 text-xs text-red-600 dark:text-red-400">{s.reason}</span>}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex gap-3">
                <button onClick={onBack} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 text-gray-700 dark:text-gray-300">Voltar</button>
                <button onClick={handleConfirm} disabled={validatingSigners || !allSignersValid || submitting}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                    {validatingSigners ? 'Validando...' : 'Confirmar e Prosseguir'}
                </button>
            </div>
        </div>
    );
};

export default ConfirmStep;
